// --- React & Redux
import React from 'react';
import { useSelector } from 'react-redux';
// --- External UI Libraries
import { Col, Row } from 'antd';
import Button from '@dtci/spellbook/Button';
// --- API & Utils
import { datastageApi } from 'datastage/api';
import { filterCriteria } from 'datastage/utils/filterCriteria';
import { getItemRange } from 'datastage/utils/pagination';
// --- Hooks & Store
import { useJobTaskActions } from 'datastage/store/jobTasks/useJobTasks';
import { useLoading } from 'datastage/utils/hooks/useLoading';
import { useToasts } from 'datastage/utils/hooks/useToasts';
// --- Components
import { DetailsModal } from 'datastage/components/DetailsModal';
import { Pagination } from 'datastage/components/Pagination';
import { JobTasksGrid } from './grid';

/**
 * Job Tasks Modal
 *
 * Features:
 * - Displays a modal with job tasks in a grid
 * - Supports pagination to navigate through tasks
 * - Fetches task data from API based on filters and sorting
 *
 * @returns {Element}
 * @constructor
 */
export const JobTasksModal = () => {
    const {
        isModalOpen,
        filterForm,
        sortParam,
        totalPages,
        currentPage,
        itemsPerPage,
    } = useSelector((state) => state.jobTasks);

    const { updateJobTasks, clearJobTasks } = useJobTaskActions();
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { showErrorToast } = useToasts()

    const onCloseModal = () => {
        clearJobTasks()
    };

    // Handle page change in pagination
    const handlePageChange = async (e) => {
        const pageNumber = typeof e === 'number' ? e : e?.selected;
        //if (pageNumber === currentPage) return;
        setLoadingTrue();
        try {
            const nextPageRange = getItemRange(pageNumber, itemsPerPage);
            const data = await datastageApi.jobTasks(
                filterCriteria(filterForm, '', 'jobTasks'),
                nextPageRange,
                sortParam,
            );

            if (data?.code) {
                showErrorToast(data?.message || 'Failed to fetch job tasks data.');
                return;
            }
            updateJobTasks({
                tasks: data?.content,
                totalPages: data?.totalPages,
                totalItems: data?.totalElements,
                currentPage: data?.pageable?.pageNumber
            });
        } catch (error) {
            showErrorToast(error?.message || 'Failed to fetch job tasks data.');
            console.error('Error fetching job tasks:', error);
        } finally {
            setLoadingFalse();
        }
    };

        // Refresh the calculations list
    const handleRefresh = () => {
        handlePageChange(currentPage);
    }

    return (
       <DetailsModal
           modalHeader={
                <Row style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                    <Col style={{ flex: 1 }}>
                        <span style={{ fontSize: 18, fontWeight: 500 }}>Job Tasks</span>
                    </Col>
                    <Col style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 15, marginRight: 25, justifyContent: 'flex-end' }}>
                        <Button
                            iconType="fa-solid fa-rotate"
                            labelText="Refresh"
                            styleType="link"
                            onClick={handleRefresh}
                        >
                            Refresh
                        </Button>
                    </Col>
                </Row>
           }
            isOpen={isModalOpen}
            onClose={onCloseModal}
            footer={
                <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Pagination
                        pageCount={totalPages}
                        onPageChange={handlePageChange}
                        previousLabel="←"
                        nextLabel="→"
                        forcePage={currentPage}
                    />
                </div>
            }
        >
            
            <JobTasksGrid />
        </DetailsModal>
    );
};
